import { MAX_AUDIBLE_FREQ_HZ, MAX_RECIPE_PEAK_GAIN, MIN_AUDIBLE_FREQ_HZ } from "./recipeValidation";
import type { CandidateLayer, DelayTap, GainEnvelope, NoiseLayer, OscillatorShape, SynthLayer, ToneLayer } from "./types";

/**
 * Promoted from `src/audio/lab/round2/recipeHelpers.ts` - the structural
 * building blocks behind SNAKESCORE C-BASE. Listening Round 1 found that a
 * single generic envelope/tone shape reused across every event (varying
 * only oscillator waveform, filtering, and a small loudness trim) reads as
 * "the same sound recolored," not a distinct identity. Each function here
 * produces a genuinely different STRUCTURE - layer count, envelope
 * attack/release ratio, tonal-vs-noise balance, presence of an echo tail -
 * not just different numbers plugged into one shape. Both production's
 * approved catalog (`src/audio/catalog/`) and the QA lab's unselected
 * candidates are built from these same primitives.
 */

/** SNAKESCORE C-BASE's reference pitch. */
export const C_BASE_TONIC_HZ = 440;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function clampFreq(hz: number): number {
  return clamp(hz, MIN_AUDIBLE_FREQ_HZ, MAX_AUDIBLE_FREQ_HZ);
}

export function noteHz(semitones: number, tonicHz: number = C_BASE_TONIC_HZ): number {
  return clampFreq(tonicHz * Math.pow(2, semitones / 12));
}

function tone(shape: OscillatorShape, startHz: number, endHz: number, mix = 1): ToneLayer {
  return { shape, startFreqHz: clampFreq(startHz), endFreqHz: clampFreq(endHz), mix: clamp(mix, 0, 1) };
}

function noiseLayer(filterType: NoiseLayer["filterType"], freqHz: number, q: number, mix: number): NoiseLayer {
  return { filterType, filterFreqHz: clampFreq(freqHz), filterQ: q, mix: clamp(mix, 0, 1) };
}

function envelope(attackMs: number, decayMs: number, sustainLevel: number, sustainMs: number, releaseMs: number, peakGain: number): GainEnvelope {
  return {
    attackMs: Math.max(1, attackMs),
    decayMs: Math.max(0, decayMs),
    sustainLevel: clamp(sustainLevel, 0, 1),
    sustainMs: Math.max(0, sustainMs),
    releaseMs: Math.max(1, releaseMs),
    peakGain: clamp(peakGain, 0.02, MAX_RECIPE_PEAK_GAIN),
  };
}

interface SynthOpts {
  durationMs: number;
  tones: ToneLayer[];
  noise?: NoiseLayer;
  envelope: GainEnvelope;
  pan?: number;
  delayTaps?: DelayTap[];
  isLoopSample?: boolean;
  loudness: number;
}

function synth(opts: SynthOpts): SynthLayer {
  return {
    durationMs: opts.durationMs,
    tones: opts.tones,
    noise: opts.noise,
    envelope: opts.envelope,
    pan: clamp(opts.pan ?? 0, -1, 1),
    delayTaps: opts.delayTaps,
    isLoopSample: opts.isLoopSample,
    perceptualLoudness: clamp(opts.loudness, 0, 1),
  };
}

/** Wraps a `SynthLayer` into a scheduled `CandidateLayer` at the given timeline offset. */
export function layer(atMs: number, synthLayer: SynthLayer, label: string, transposeSemitones?: number): CandidateLayer {
  return { atMs, synth: synthLayer, label, transposeSemitones };
}

/** A labeled silent gap - no sound, standing in for compressed real elapsed time. */
export function silentGap(atMs: number, label: string): CandidateLayer {
  return { atMs, synth: synth({ durationMs: 1, tones: [tone("sine", 440, 440, 0)], envelope: envelope(1, 0, 0, 0, 1, 0.02), loudness: 0 }), label, silent: true };
}

// --- Structural archetypes -------------------------------------------------

/** A short, dry, single-tone pluck - no noise, quick attack/decay, minimal tail. Delicate/appearance moments. */
export function pluck(semitone: number, durationMs: number, loudness: number, pan = 0): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sine", noteHz(semitone), noteHz(semitone + 1))],
    envelope: envelope(3, durationMs * 0.25, 0.1, durationMs * 0.1, durationMs * 0.5, loudness * 0.5),
    pan,
    loudness,
  });
}

/** A physical percussive hit - tone plus a noise transient, very fast attack, tight decay, no sustain. Impacts/pickups/collisions. */
export function impact(semitone: number, durationMs: number, loudness: number, pan = 0, noiseFreq = 1200): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("triangle", noteHz(semitone), noteHz(semitone - 2))],
    noise: noiseLayer("bandpass", noiseFreq, 1.5, 0.35),
    envelope: envelope(2, durationMs * 0.3, 0.05, 0, durationMs * 0.6, loudness * 0.55),
    pan,
    loudness,
  });
}

/** Several simultaneous tone layers rising together - a real chord, not one tone recolored. Premium reward moments. */
export function risingChord(semitones: number[], durationMs: number, loudness: number, pan = 0): SynthLayer {
  return synth({
    durationMs,
    tones: semitones.map((s, i) => tone("triangle", noteHz(s), noteHz(s + 3), i === 0 ? 1 : 0.55)),
    envelope: envelope(durationMs * 0.15, durationMs * 0.25, 0.35, durationMs * 0.1, durationMs * 0.5, loudness * 0.55),
    pan,
    loudness,
  });
}

/** A brief, near-silent inward suction - fast downward glide at low gain. Anticipation/vacuum moments that must stay short and never feel like gameplay stopped. */
export function suction(semitone: number, durationMs: number, loudness: number, pan = 0): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sine", noteHz(semitone), noteHz(semitone - 12))],
    noise: noiseLayer("highpass", 2500, 0.6, 0.15),
    envelope: envelope(durationMs * 0.4, durationMs * 0.3, 0.05, 0, durationMs * 0.3, loudness * 0.3),
    pan,
    loudness,
  });
}

/** A soft harmonic swell - slow attack, wide release, an airy echo tail. Warm expansions and gate blooms. */
export function bloom(semitones: number[], durationMs: number, loudness: number, pan = 0, taps: DelayTap[] = [{ delayMs: 120, gain: 0.22 }, { delayMs: 260, gain: 0.1 }]): SynthLayer {
  return synth({
    durationMs,
    tones: semitones.map((s, i) => tone("sine", noteHz(s), noteHz(s + 2), i === 0 ? 1 : 0.4)),
    envelope: envelope(durationMs * 0.3, durationMs * 0.2, 0.4, durationMs * 0.15, durationMs * 0.6, loudness * 0.5),
    pan,
    delayTaps: taps,
    loudness,
  });
}

/** A bright, brief high-register accent with its own echo tail. Sparkle/confirmation moments. */
export function sparkle(semitone: number, durationMs: number, loudness: number, pan = 0): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sine", noteHz(semitone + 12), noteHz(semitone + 14))],
    envelope: envelope(2, durationMs * 0.2, 0.1, 0, durationMs * 0.7, loudness * 0.4),
    pan,
    delayTaps: [{ delayMs: 70, gain: 0.25 }, { delayMs: 150, gain: 0.1 }],
    loudness,
  });
}

/** Filtered noise carries the identity - the tonal layer is present only to satisfy "at least one tone" but mixed near-inaudible. Dry mechanical/wind/ignition texture - never a tonal "portal" character. */
export function windTexture(durationMs: number, loudness: number, pan = 0, freqHz = 2400, filterType: NoiseLayer["filterType"] = "highpass", q = 0.8): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("square", 40, 40, 0.001)],
    noise: noiseLayer(filterType, freqHz, q, 0.6),
    envelope: envelope(durationMs * 0.1, durationMs * 0.2, 0.5, durationMs * 0.2, durationMs * 0.3, loudness * 0.45),
    pan,
    loudness,
  });
}

/** Two slightly detuned tone layers gliding together plus filtered noise - a curved, phase-y "bent space" identity. Candidates convey travel by scheduling two of these at different `pan` values across the timeline, since pan is static within one layer. */
export function portalBend(semitone: number, durationMs: number, loudness: number, pan = 0): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sine", noteHz(semitone), noteHz(semitone + 9)), tone("triangle", noteHz(semitone - 0.15), noteHz(semitone + 8.7), 0.5)],
    noise: noiseLayer("bandpass", 1200, 2, 0.15),
    envelope: envelope(durationMs * 0.25, durationMs * 0.25, 0.4, durationMs * 0.2, durationMs * 0.5, loudness * 0.45),
    pan,
    delayTaps: [{ delayMs: 140, gain: 0.2 }, { delayMs: 300, gain: 0.08 }],
    loudness,
  });
}

/** A tight forward ignition - a fast-rising tone plus a bright noise burst, dry short tail, centered. SPEED TRIAL's identity - never a wide/airy tail. */
export function ignition(semitone: number, durationMs: number, loudness: number): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sawtooth", noteHz(semitone), noteHz(semitone + 7))],
    noise: noiseLayer("highpass", 3000, 0.7, 0.4),
    envelope: envelope(3, durationMs * 0.2, 0.2, durationMs * 0.1, durationMs * 0.3, loudness * 0.55),
    pan: 0,
    loudness,
  });
}

/** A rhythmic, centered, dry short pulse - SPEED TRIAL's "urgent timer" warning, as opposed to WORMHOLE's spatial pulse below. No delay taps, no pan movement. */
export function timerPulse(semitone: number, durationMs: number, loudness: number): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("square", noteHz(semitone), noteHz(semitone))],
    noise: noiseLayer("highpass", 2600, 1, 0.25),
    envelope: envelope(2, durationMs * 0.2, 0, 0, durationMs * 0.3, loudness * 0.5),
    pan: 0,
    loudness,
  });
}

/** A slower, softer, wider pulse with its own short echo tail - WORMHOLE's own spatial warning identity, distinct from SPEED TRIAL's dry `timerPulse`. */
export function portalPulse(semitone: number, durationMs: number, loudness: number, pan = 0): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sine", noteHz(semitone), noteHz(semitone - 3))],
    noise: noiseLayer("bandpass", 1000, 1.5, 0.2),
    envelope: envelope(durationMs * 0.3, durationMs * 0.2, 0.3, durationMs * 0.1, durationMs * 0.5, loudness * 0.4),
    pan,
    delayTaps: [{ delayMs: 160, gain: 0.18 }],
    loudness,
  });
}

/** A clean downward sweep - single tone, no noise, rapid descent, minimal resonance/tail. */
export function descend(semitoneFrom: number, semitoneTo: number, durationMs: number, loudness: number): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("sine", noteHz(semitoneFrom), noteHz(semitoneTo))],
    envelope: envelope(2, durationMs * 0.3, 0.15, 0, durationMs * 0.55, loudness * 0.5),
    pan: 0,
    loudness,
  });
}

/** A sharp noise-forward collision transient with a compact tail - no sustain, near-instant attack. */
export function collisionSnap(semitone: number, durationMs: number, loudness: number): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("triangle", noteHz(semitone), noteHz(semitone - 8))],
    noise: noiseLayer("bandpass", 900, 1.2, 0.4),
    envelope: envelope(1, durationMs * 0.2, 0, 0, durationMs * 0.7, loudness * 0.55),
    pan: 0,
    loudness,
  });
}

/** A rapid, resonance-free descending shutdown - the driest of the descending shapes (no noise, near-zero sustain). */
export function powerDown(semitoneFrom: number, semitoneTo: number, durationMs: number, loudness: number): SynthLayer {
  return synth({
    durationMs,
    tones: [tone("square", noteHz(semitoneFrom), noteHz(semitoneTo), 0.8), tone("sine", noteHz(semitoneFrom), noteHz(semitoneTo), 0.5)],
    envelope: envelope(1, durationMs * 0.4, 0, 0, durationMs * 0.2, loudness * 0.45),
    pan: 0,
    loudness,
  });
}
